import * as RadixPopover from '@radix-ui/react-popover'
import type { PopoverProps } from './Popover.types'

/**
 * popover — trigger + floating panel composite.
 *
 * The caller's trigger gets the popover behavior merged onto it (as-child, no
 * wrapper element); the panel portals to the document body, start-aligned
 * beneath the trigger with a small offset. Open state is caller-controlled
 * when `open` is supplied, otherwise managed internally by the confined
 * library — absent open/onOpenChange props are NOT passed through, so
 * supplying neither keeps true uncontrolled behavior. The panel re-asserts its
 * own pointer events (`pointer-events-auto`) so it stays interactive when
 * hosted inside a modal dialog that disables pointer events on the body.
 *
 * Modal selector mode (`modal`): a light backdrop dims the rest of the page
 * and the library confines interaction to the panel — including scroll, so a
 * host dialog's page-scroll lock can no longer swallow wheel events aimed at
 * the panel's own scroll areas. Clicking the dimmed backdrop is an outside
 * click and closes as usual. The backdrop is lighter than the dialog
 * overlay's — a selector focuses attention, it does not change context.
 * @radix-ui/react-popover is confined to this unit.
 *
 * ⚠️ THE PANEL SITS ON THE OVERLAY LAYER, NOT ON PAINT ORDER ALONE. A popover
 * opened from inside a modal must draw ON TOP of it: an unlayered panel opened,
 * took focus and rendered behind the dialog, invisible. Both portals name the
 * same layer as the dialog and win on DOM order instead. The z-index goes on
 * the content, which the library copies onto its own positioned wrapper.
 */
/**
 * How wide the panel is drawn. `compact` is the default popover, sized for a
 * list of options; `wide` is for a panel holding FIELDS side by side — two date
 * boxes in a compact panel cut their own text off. It never exceeds the
 * viewport, so the wide panel is still a panel on a phone.
 */
const PANEL_WIDTH: Readonly<Record<'compact' | 'wide', string>> = {
  compact: 'w-64',
  wide: 'w-[min(23rem,calc(100vw-2rem))]',
}

export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  matchTriggerWidth = false,
  width = 'compact',
  restoreFocusOnClose = true,
  focusPanelOnOpen = true,
  modal = false,
}: PopoverProps) {
  const rootProps = {
    ...(open !== undefined ? { open } : {}),
    ...(onOpenChange !== undefined ? { onOpenChange } : {}),
    ...(modal ? { modal: true } : {}),
  }
  // Suppress the library's automatic focus moves only where the caller opted
  // out: the close-time return (chained/auto-advancing panels) and the
  // open-time entry (a panel that opened on hover rather than on purpose).
  const contentProps = {
    ...(restoreFocusOnClose ? {} : { onCloseAutoFocus: (event: Event) => event.preventDefault() }),
    ...(focusPanelOnOpen ? {} : { onOpenAutoFocus: (event: Event) => event.preventDefault() }),
  }
  return (
    <RadixPopover.Root {...rootProps}>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      {/* The modal backdrop: its own portal mounted before the content's (the
          library portals a single child each), so the panel paints above it;
          being outside the content, a click on it counts as an outside
          interaction and closes the popover. */}
      {modal ? (
        <RadixPopover.Portal>
          <div aria-hidden="true" className="fixed inset-0 z-50 bg-text/20" />
        </RadixPopover.Portal>
      ) : null}
      <RadixPopover.Portal>
        <RadixPopover.Content
          align="start"
          sideOffset={4}
          {...contentProps}
          className={`pointer-events-auto z-50 rounded-lg border border-border bg-surface p-3 shadow-overlay ${
            matchTriggerWidth ? 'w-(--radix-popover-trigger-width)' : PANEL_WIDTH[width]
          }`}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}
